import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { DragDropTable, TableColumn, SortConfig } from '../../components/ui/drag-drop-table';
import { EnhancedPagination } from '../../components/ui/enhanced-pagination';
import { useUIStore } from '../../stores/ui-store';
import { BlockForm } from './BlockForm';
import { ApiErrorFallback } from '../../components/common/ApiErrorFallback';
import { LoadingState } from '../../components/common/LoadingState';
import { EmptyState } from '../../components/common/EmptyState';
import {
  usePlatformBlockList,
  usePlatformBlockDetail,
  useDeletePlatformBlock,
  useReactivatePlatformBlock,
  useReorderPlatformBlocks,
  useExportPlatformBlocksCsv,
  useImportPlatformBlocksCsv
} from './api';
import {
  PlatformBlockSummaryResponse,
  PlatformBlockDetailResponse,
  getBlockCategoryName,
  getDifficultyLevelName,
  BlockCategory,
  DifficultyLevel
} from './types';

export function PlatformBlocksPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<BlockCategory | undefined>();
  const [selectedDifficulty, setSelectedDifficulty] = useState<DifficultyLevel | undefined>();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState<number | undefined>();
  const [editingBlock, setEditingBlock] = useState<PlatformBlockDetailResponse | undefined>();
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'name', direction: 'asc' });

  const { addNotification } = useUIStore();
  const deleteMutation = useDeletePlatformBlock();
  const reactivateMutation = useReactivatePlatformBlock();
  const reorderMutation = useReorderPlatformBlocks();
  const exportCsvMutation = useExportPlatformBlocksCsv();
  const importCsvMutation = useImportPlatformBlocksCsv();

  // Fetch blocks list with current filters
  const { data, isLoading, error, refetch } = usePlatformBlockList({
    page: currentPage,
    pageSize: pageSize,
    search: searchTerm || undefined,
    category: selectedCategory,
    difficulty: selectedDifficulty,
    includeInactive,
    sortBy: sortConfig.field,
    sortDirection: sortConfig.direction,
  });

  // Fetch detailed block for editing
  const { data: blockDetail } = usePlatformBlockDetail(editingBlockId);

  // Effect to handle when block detail is loaded
  useEffect(() => {
    if (blockDetail && editingBlockId) {
      setEditingBlock(blockDetail);
      setIsFormOpen(true);
    }
  }, [blockDetail, editingBlockId]);

  const handleCreateNew = () => {
    setEditingBlockId(undefined);
    setEditingBlock(undefined);
    setFormMode('create');
    setIsFormOpen(true);
  };

  const handleEdit = (block: PlatformBlockSummaryResponse) => {
    setEditingBlockId(block.id);
    setFormMode('edit');
    // The modal will open automatically when the block detail loads via useEffect
  };

  const handleDelete = async (block: PlatformBlockSummaryResponse) => {
    if (!window.confirm(`Are you sure you want to deactivate "${block.name}"?`)) {
      return;
    }

    try {
      await deleteMutation.mutateAsync({ id: block.id });
      addNotification({
        type: 'success',
        title: 'Block Deactivated',
        message: `${block.name} has been deactivated successfully.`,
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Deactivate Block',
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleReactivate = async (block: PlatformBlockSummaryResponse) => {
    try {
      await reactivateMutation.mutateAsync({ id: block.id });
      addNotification({
        type: 'success',
        title: 'Block Reactivated',
        message: `${block.name} has been reactivated successfully.`,
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Reactivate Block',
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleReorder = async (reorderedBlocks: PlatformBlockSummaryResponse[]) => {
    const blockIds = reorderedBlocks.map(block => block.id);
    
    try {
      await reorderMutation.mutateAsync({ blockIds });
      addNotification({
        type: 'success',
        title: 'Blocks Reordered',
        message: 'Block order has been updated successfully.',
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Reorder Blocks',
        message: error.message || 'An unexpected error occurred.',
      });
      // Refetch to restore original order
      refetch();
    }
  };

  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Define table columns for the drag and drop table
  const columns: TableColumn<PlatformBlockSummaryResponse>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (block) => (
        <div>
          <div className="font-semibold text-slate-900">{block.name}</div>
          {block.tags && (
            <div className="text-xs text-slate-500 mt-1 font-medium">
              {block.tags}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      render: (block) => (
        block.description ? (
          <div className="text-sm text-slate-600 leading-relaxed">
            {block.description.length > 60
              ? `${block.description.substring(0, 60)}...`
              : block.description}
          </div>
        ) : (
          <span className="text-slate-400 text-sm italic">No description</span>
        )
      ),
    },
    {
      key: 'duration',
      label: 'Duration',
      sortable: true,
      render: (block) => (
        <div className="inline-flex items-center px-2.5 py-1 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium">
          {block.durationMinutes} min
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (block) => (
        <span className="status-badge info">
          {getBlockCategoryName(block.category)}
        </span>
      ),
    },
    {
      key: 'difficulty',
      label: 'Difficulty',
      sortable: true,
      render: (block) => (
        <span className={`status-badge ${
          block.difficulty === DifficultyLevel.Beginner ? 'active' :
          block.difficulty === DifficultyLevel.Intermediate ? 'warning' :
          'danger'
        }`}>
          {getDifficultyLevelName(block.difficulty)}
        </span>
      ),
    },
    {
      key: 'exercises',
      label: 'Exercises',
      sortable: true,
      render: (block) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-purple-100 text-purple-700 rounded-lg flex items-center justify-center text-sm font-semibold">
            {block.exerciseCount}
          </div>
          <span className="text-xs text-slate-500 font-medium">
            exercise{block.exerciseCount !== 1 ? 's' : ''}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (block) => (
        <span className={`status-badge ${block.isActive ? 'active' : 'inactive'}`}>
          {block.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    refetch();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory(undefined);
    setSelectedDifficulty(undefined);
    setIncludeInactive(false);
    setCurrentPage(1);
  };

  const handleCsvExport = async () => {
    try {
      const blob = await exportCsvMutation.mutateAsync({
        includeInactive,
        search: searchTerm || undefined,
        category: selectedCategory,
        difficulty: selectedDifficulty,
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `platform_blocks_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: error.message || 'Failed to export block data.',
      });
    }
  };

  const handleCsvImport = async (file: File) => {
    const result = await importCsvMutation.mutateAsync({ file });
    return {
      created: result.summary.created,
      updated: result.summary.updated,
      skipped: result.summary.skipped,
      errors: result.errors,
    };
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      addNotification({
        type: 'error',
        title: 'Invalid File Type',
        message: 'Please select a CSV file.',
      });
      return;
    }

    try {
      const result = await handleCsvImport(file);
      
      addNotification({
        type: 'success',
        title: 'Import Successful',
        message: `Created: ${result.created}, Updated: ${result.updated}, Skipped: ${result.skipped}`,
      });

      if (result.errors.length > 0) {
        console.warn('Import warnings:', result.errors);
        addNotification({
          type: 'warning',
          title: 'Import Warnings',
          message: `${result.errors.length} items had issues. Check console for details.`,
        });
      }
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Import Failed',
        message: error.message || 'An unexpected error occurred during import.',
      });
    } finally {
      // Reset file input
      const input = document.getElementById('csv-import-input') as HTMLInputElement;
      if (input) {
        input.value = '';
      }
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Platform Session Blocks</h1>
        </div>
        <ApiErrorFallback 
          error={error}
          onRetry={() => refetch()}
          title="Unable to load blocks"
          description="There was a problem loading the session blocks list."
          showDetails={true}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Session Blocks</h1>
          <p className="text-gray-600 text-sm">
            Manage reusable exercise blocks available to all studios
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={handleCsvExport}
            variant="outline"
            size="sm"
            disabled={exportCsvMutation.isPending}
            className="flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{exportCsvMutation.isPending ? 'Exporting...' : 'Export CSV'}</span>
          </Button>
          <Button
            onClick={() => document.getElementById('csv-import-input')?.click()}
            variant="outline"
            size="sm"
            disabled={importCsvMutation.isPending}
            className="flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            <span>{importCsvMutation.isPending ? 'Importing...' : 'Import CSV'}</span>
          </Button>
          <Button onClick={handleCreateNew}>
            Create New Block
          </Button>
        </div>
      </div>

      {/* Hidden file input for CSV import */}
      <input
        id="csv-import-input"
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="py-3">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-center">
            <div className="flex-1 min-w-[200px]">
              <Input
                id="search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search blocks..."
                className="h-8 text-sm"
              />
            </div>
            
            <select
              id="category"
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) as BlockCategory : undefined)}
              className="h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]"
            >
              <option value="">All Categories</option>
              {Object.values(BlockCategory)
                .filter(value => typeof value === 'number')
                .map((category) => (
                  <option key={category} value={category}>
                    {getBlockCategoryName(category as BlockCategory)}
                  </option>
                ))}
            </select>

            <select
              id="difficulty"
              value={selectedDifficulty || ''}
              onChange={(e) => setSelectedDifficulty(e.target.value ? parseInt(e.target.value) as DifficultyLevel : undefined)}
              className="h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]"
            >
              <option value="">All Difficulties</option>
              {Object.values(DifficultyLevel)
                .filter(value => typeof value === 'number')
                .map((difficulty) => (
                  <option key={difficulty} value={difficulty}>
                    {getDifficultyLevelName(difficulty as DifficultyLevel)}
                  </option>
                ))}
            </select>

            <div className="flex items-center space-x-1">
              <input
                id="includeInactive"
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="includeInactive" className="text-xs text-gray-700 whitespace-nowrap">
                Inactive
              </label>
            </div>

            <div className="flex space-x-1">
              <Button type="submit" variant="outline" size="sm" className="h-8 px-3">
                Search
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-8 px-3" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>


      {/* Blocks Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingState message="Loading blocks..." />
          ) : !data?.blocks.length ? (
            <EmptyState
              title="No blocks found"
              description={
                searchTerm || selectedCategory || selectedDifficulty || includeInactive
                  ? 'Try adjusting your filters or create new blocks.'
                  : 'Get started by creating your first session block.'
              }
              actionLabel="Create New Block"
              onAction={handleCreateNew}
              showCard={false}
            />
          ) : (
            <>
              {/* Top Pagination - Compact */}
              {data.pagination && (
                <div className="flex justify-between items-center mb-4 py-2 border-b border-gray-100">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>
                      Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, data.pagination.totalCount)} of {data.pagination.totalCount}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                      className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10 per page</option>
                      <option value={15}>15 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1 || isLoading}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-2 py-1 text-sm">
                        {currentPage} of {data.pagination.totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(Math.min(data.pagination.totalPages, currentPage + 1))}
                        disabled={currentPage >= data.pagination.totalPages || isLoading}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <DragDropTable
                items={data.blocks}
                columns={columns}
                keyExtractor={(block) => block.id}
                onReorder={handleReorder}
                isLoading={reorderMutation.isPending}
                sortConfig={sortConfig}
                onSort={handleSort}
                renderActions={(block) => (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(block)}
                    >
                      Edit
                    </Button>
                    {block.isActive ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(block)}
                        disabled={deleteMutation.isPending}
                      >
                        {deleteMutation.isPending ? 'Deactivating...' : 'Deactivate'}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleReactivate(block)}
                        disabled={reactivateMutation.isPending}
                      >
                        {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate'}
                      </Button>
                    )}
                  </>
                )}
              />

              {/* Enhanced Pagination */}
              {data.pagination && (
                <EnhancedPagination
                  pagination={data.pagination}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={handlePageSizeChange}
                  isLoading={isLoading}
                  className="mt-4"
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Block Form Modal */}
      <BlockForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingBlockId(undefined);
          setEditingBlock(undefined);
        }}
        block={editingBlock}
        mode={formMode}
      />
    </div>
  );
}